// ---------------------------------------------------------------------------
// Granular synthesis engine (one-shot burst, for Z-scale tick hover)
// ---------------------------------------------------------------------------

/**
 * Fires a cloud of short grains, each with its own ADSR, slight pitch and
 * timing randomisation. Uses only OscillatorNode + GainNode (no BufferSource),
 * so it runs entirely within the Web Audio API graph.
 *
 * @param {AudioContext} ctx
 * @param {AudioNode}    destination
 * @param {number}       ratio        - Data ratio [0,1] → pitch centre + density
 * @param {object}       cfg
 */
export function fireGranularBurst(ctx, destination, ratio = 0.5, cfg = {}) {
    const {
        grainCount = 18,
        baseFreqMin = 5,
        baseFreqMax = 250,
        pitchScatter = 0.15, // ±fraction of base freq
        timeScatter = 0.06, // ±seconds of onset jitter
        grainDuration = 0.07, // seconds per grain (attack+decay; no sustain)
        peakGain = 0.12,
        burstGain = 0.7, // master gain for the entire burst
        grainType = "sine",
    } = cfg;

    const baseFreq = baseFreqMax - ratio * (baseFreqMax - baseFreqMin);

    // Master gain for the burst (so overall level is manageable)
    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(burstGain, ctx.currentTime);
    masterGain.connect(destination);

    for (let i = 0; i < grainCount; i++) {
        // Random onset within the scatter window
        const onset = ctx.currentTime + Math.random() * timeScatter;

        // Pitch jitter: ±pitchScatter fraction around baseFreq
        const pitchMult = 1 + (Math.random() * 2 - 1) * pitchScatter;
        const freq = baseFreq * pitchMult;

        // Each grain: oscillator → grainGain → masterGain
        const grainGain = ctx.createGain();
        grainGain.gain.setValueAtTime(0, onset);
        grainGain.connect(masterGain);

        const osc = ctx.createOscillator();
        osc.type = grainType;
        osc.frequency.setValueAtTime(freq, onset);
        osc.connect(grainGain);

        const halfDur = grainDuration / 2;

        // Triangle envelope over grain duration (attack then immediate decay)
        grainGain.gain.linearRampToValueAtTime(peakGain, onset + halfDur);
        grainGain.gain.linearRampToValueAtTime(0, onset + grainDuration);

        osc.start(onset);
        osc.stop(onset + grainDuration + 0.01);
    }

    // Tear down master gain after the last grain could possibly finish
    const teardownDelay = (timeScatter + grainDuration + 0.05) * 1000;
    setTimeout(() => {
        masterGain.disconnect();
    }, teardownDelay);
}
